import { useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Badge, Box, Grid, MenuItem, Stack, Tab, Tabs, Typography } from '@mui/material'

import { useTranslation } from 'react-i18next'
import { ParentSize } from '@visx/responsive'
import { LIMIT } from '../schema'

import FormTextInput from 'packages/ui/TextField'
import FormDateInput from 'packages/ui/DateRange'
import ActionButtons from '../ActionButtons'
import TranslationButton from './TranslationButton'

const ContentStep = ({ loading, onSubmit }) => {
  const { t } = useTranslation()
  const [selectedTab, setSelectedTab] = useState(0)
  const { control, trigger, register, formState } = useFormContext()
  const { fields, replace } = useFieldArray({
    control,
    name: 'contents'
  })
  const hasError = Boolean(
    formState.errors.privateTitle ||
    formState.errors.publicTitle ||
    formState.errors.contentCompleted ||
    formState.errors.contents
  )
  const handleTableChange = index => {
    setSelectedTab(index)
  }

  return (
    <Stack alignItems="center" justifyContent="space-between" height="100%" pb={2}>
      <input type={'hidden'} {...register('contentCompleted')} />
      <ParentSize>
        {({ height }) => (
          <Grid container spacing={1} alignItems="center" sx={{ maxHeight: height, overflowY: 'auto' }}>
            <Grid item xs={6}>
              <FormTextInput
                name="name"
                label={t('advertisements.edition.content.main_title')}
                placeholder={t('advertisements.edition.content.main_title_placeholder')}
                maxSize={LIMIT.maxPublicTitleLength}
                required
              />
            </Grid>
            <Grid item xs={6} sx={{marginTop: '6px'}}>
              <FormDateInput label={t('advertisements.edition.content.deploy_date')} required />
            </Grid>
            <Grid item xs={12}>
              <FormTextInput
                name={'fallback_language'}
                control={control}
                select
                label={t('auth.admin.edit.fallback_language_placeholder')}>
                {fields.map(field => (
                  <MenuItem key={field.language} value={field.language} sx={{ fontSize: '12px' }}>
                    {t(`languages.${field.language}`)}
                  </MenuItem>
                ))}
              </FormTextInput>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Tabs
                  value={selectedTab}
                  variant="fullWidth"
                  sx={{
                    borderRadius: '6px',
                    background: '#F6F7F8',
                    height: '32px',
                    border: 'solid 1px #E8E8E8',
                    marginBottom: '20px'
                  }}
                  TabIndicatorProps={{
                    style: { display: 'none' }
                  }}>
                  {fields.map((field, index) => (
                    <Tab
                      key={`tab-${field.language}`}
                      sx={{
                        minWidth: 70,
                        padding: 0,
                        minHeight: '32px',
                        height: '32px',
                        ...(index === selectedTab ? { boxShadow: '0px 2px 4px 0px #00000014' } : {})
                      }}
                      label={
                        <Box
                          sx={{
                            boxSizing: 'border-box',
                            ...(index === selectedTab
                              ? {
                                  background: 'white',
                                  p: 2,
                                  borderRadius: '4px',
                                  width: '100%',
                                  boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)'
                                }
                              : {})
                          }}>
                          <Badge
                            variant="dot"
                            invisible={!formState.errors.contents?.[index]}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: '#DC2626',
                                right: '-9px'
                              }
                            }}>
                            <Typography
                              sx={{
                                fontSize: '12px',
                                textTransform: 'capitalize',
                                p: 0,
                                ...(index === selectedTab ? { fontWeight: 600 } : {})
                              }}>
                              {t(`languages.${field.language}`)}
                            </Typography>
                          </Badge>
                        </Box>
                      }
                      onClick={() => handleTableChange(index)}
                    />
                  ))}
                </Tabs>
                <Box>
                  {fields.map((f, index) => (
                    <Box key={f.id} sx={{ display: selectedTab === index ? 'flex' : 'none' }}>
                      <Grid container columnSpacing={1}>
                        <Grid item xs={12}>
                          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'flex-start'} gap={2}>
                            <Box sx={{ flex: 1 }}>
                              <FormTextInput
                                name={`contents.${index}.header`}
                                control={control}
                                onChange={() => trigger('contentCompleted')}
                                forceError={formState.errors.contentCompleted ? t('ui_elements.required_field') : null}
                                label={t('advertisements.edition.content.public_title')}
                                placeholder={t('advertisements.edition.content.public_title_placeholder')}
                                maxSize={LIMIT.maxPublicTitleLength}
                                required
                              />
                            </Box>
                            <TranslationButton selectedTab={selectedTab} replace={replace} />
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <FormTextInput
                            name={`contents.${index}.body`}
                            onChange={() => trigger('contentCompleted')}
                            forceError={formState.errors.contentCompleted ? t('ui_elements.required_field') : null}
                            label={t('advertisements.edition.content.advertisementContent')}
                            placeholder={t('advertisements.edition.content.advertisementContent_placeholder')}
                            maxSize={LIMIT.maxAdvertisementContentLength}
                            required
                            multiline
                            InputProps={{
                              sx: {
                                '& textarea': {
                                  height: '132px !important'
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormTextInput
                            name={`contents.${index}.footer`}
                            label={t('advertisements.edition.content.signature')}
                            placeholder={t('advertisements.edition.content.signature_placeholder')}
                            maxSize={LIMIT.maxSignatureLength}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </ParentSize>
      <ActionButtons
        loading={loading}
        onSubmit={onSubmit}
        disabled={hasError}
        labelSubmit={t('ui_elements.form_next')}
      />
    </Stack>
  )
}

export default ContentStep
